import React, { useState } from 'react';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import { FaDownload } from 'react-icons/fa';
import logo from '../../images/logo-long.png';
import '../../scss/match/SocialPostModal.scss';

const SocialPostModal = ({ match, onClose }) => {
    const [isCapturing, setIsCapturing] = useState(false);

    const formatMatchTime = (timeString) => {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        return `${hour}${minutes !== '00' ? ':' + minutes : ''}${hour >= 12 ? 'pm' : 'am'}`;
    };

    // Get scorers from the match.scorers object with fallbacks
    const teamAName = match.team_a || 'White';
    const teamBName = match.team_b || 'Colour';
    
    const teamAScorers = 
        (match.scorers?.[teamAName]) || 
        (match.scorers?.White) || 
        (match.legacy_scorers?.White) || 
        [];
        
    const teamBScorers = 
        (match.scorers?.[teamBName]) || 
        (match.scorers?.Colour) || 
        (match.legacy_scorers?.Colour) || 
        [];
    
    // Get own goals
    const teamAOwnGoals = 
        (match.own_goals && match.own_goals[teamAName]) || 
        (match.own_goals && match.own_goals.White) || 
        [];
        
    const teamBOwnGoals = 
        (match.own_goals && match.own_goals[teamBName]) || 
        (match.own_goals && match.own_goals.Colour) || 
        [];
    
    // Calculate scores including own goals
    const oppositionOwnGoalsCount = parseInt(match.opposition_own_goals, 10) || 0;
    const cardIsHome = match.is_home === '1' || match.is_home === true || match.is_home === undefined;
    const oppositionOwnGoalLabel = match.opposition || 'Opposition';
    
    const calculateScoreWithOwnGoals = () => {
        // Get base scores
        let teamAScore = parseInt(match.score_white || '0');
        let teamBScore = parseInt(match.score_colour || '0');
        
        // Add own goals to the opposing team's score
        if (match.own_goals) {
            // Team B's own goals count for Team A
            if (teamBOwnGoals.length > 0) {
                teamBOwnGoals.forEach(og => {
                    if (og?.is_opposition) {
                        return;
                    }
                    teamAScore += parseInt(og.own_goals || 0);
                });
            }
            
            // Team A's own goals count for Team B
            if (teamAOwnGoals.length > 0) {
                teamAOwnGoals.forEach(og => {
                    if (og?.is_opposition) {
                        return;
                    }
                    teamBScore += parseInt(og.own_goals || 0);
                });
            }
        }
        
        return { teamAScore, teamBScore };
    };
    
    const finalScores = calculateScoreWithOwnGoals();

    const captureScreenshot = async () => {
        try {
            setIsCapturing(true);
            const modalElement = document.querySelector('.social-post-content');
            if (!modalElement) return;

            // Hide the download button during screenshot
            const downloadButton = modalElement.querySelector('.download-button');
            if (downloadButton) {
                downloadButton.style.display = 'none';
            }

            const canvas = await html2canvas(modalElement, {
                backgroundColor: '#000000',
                scale: 4,
                logging: false,
                useCORS: true,
                allowTaint: true
            });

            // Show the download button again
            if (downloadButton) {
                downloadButton.style.display = 'flex';
            }

            // Create download link
            const image = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = image;
            link.download = `match-result-${format(new Date(match.match_date), 'yyyy-MM-dd')}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Screenshot failed:', error);
        } finally {
            setIsCapturing(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content social-post-modal">
                <button className="modal-close" onClick={onClose}>×</button>
                <div className="social-post-content">
                    <div className="content-wrapper">
                        <div className="branding-logo">
                            <img src={logo} alt="FirstWhistle" />
                        </div>
                        <div className="team-name">{match.team_name || 'MNF'}</div>
                        <div className="title">MATCH RESULT</div>
                        <div className="match-info">
                            {format(new Date(match.match_date), 'EEE dd MMMM, yyyy')} {formatMatchTime(match.match_time)}
                        </div>
                        
                        <div className="teams">
                            <div className="team-columns">
                                <div className="team-column">
                                    <div className="team">
                                        <div className="team-name white">{teamAName.toUpperCase()}</div>
                                        <div className="score">{finalScores.teamAScore}</div>
                                    </div>
                                    <div className="scorers">
                                        {teamAScorers && teamAScorers.length > 0 && (
                                            teamAScorers.map((scorer, index) => (
                                                <div key={`score-${index}`} className="scorer">
                                                    <span className="name">{scorer.name}</span>
                                                    <span className="goals">{scorer.goals}</span>
                                                </div>
                                            ))
                                        )}
                                        {teamBOwnGoals && teamBOwnGoals.length > 0 && (
                                            teamBOwnGoals.map((scorer, index) => (
                                                <div key={`og-${index}`} className="scorer own-goal">
                                                    <span className="name">{scorer.name} (OG)</span>
                                                    <span className="goals">{scorer.own_goals}</span>
                                                </div>
                                            ))
                                        )}
                                        {cardIsHome && oppositionOwnGoalsCount > 0 && (
                                            <div className="scorer own-goal">
                                                <span className="name">{oppositionOwnGoalLabel} (OG)</span>
                                                <span className="goals">{oppositionOwnGoalsCount}</span>
                                            </div>
                                        )}
                                        {teamAScorers.length === 0 && teamBOwnGoals.length === 0 && !(cardIsHome && oppositionOwnGoalsCount > 0) && (
                                            <div className="no-scorers">—</div>
                                        )}
                                    </div>
                                </div>

                                <div className="team-column">
                                    <div className="team">
                                        <div className="team-name colour">{teamBName.toUpperCase()}</div>
                                        <div className="score">{finalScores.teamBScore}</div>
                                    </div>
                                    <div className="scorers">
                                        {teamBScorers && teamBScorers.length > 0 && (
                                            teamBScorers.map((scorer, index) => (
                                                <div key={`score-${index}`} className="scorer">
                                                    <span className="name">{scorer.name}</span>
                                                    <span className="goals">{scorer.goals}</span>
                                                </div>
                                            ))
                                        )}
                                        {teamAOwnGoals && teamAOwnGoals.length > 0 && (
                                            teamAOwnGoals.map((scorer, index) => (
                                                <div key={`og-${index}`} className="scorer own-goal">
                                                    <span className="name">{scorer.name} (OG)</span>
                                                    <span className="goals">{scorer.own_goals}</span>
                                                </div>
                                            ))
                                        )}
                                        {!cardIsHome && oppositionOwnGoalsCount > 0 && (
                                            <div className="scorer own-goal">
                                                <span className="name">{oppositionOwnGoalLabel} (OG)</span>
                                                <span className="goals">{oppositionOwnGoalsCount}</span>
                                            </div>
                                        )}
                                        {teamBScorers.length === 0 && teamAOwnGoals.length === 0 && !(!cardIsHome && oppositionOwnGoalsCount > 0) && (
                                            <div className="no-scorers">—</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button 
                            className="download-button" 
                            onClick={captureScreenshot}
                            disabled={isCapturing}
                        >
                            <FaDownload /> {isCapturing ? 'Generating...' : 'DOWNLOAD'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SocialPostModal; 